import React, { useState } from "react";
import { Card, Row, Col, Progress, Badge, Button, DatePicker, Space, Modal, List, Image } from "antd";
import { 
  AimOutlined,
  CheckCircleOutlined,
  ExclamationOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  UserOutlined,
  AlertOutlined,
  FilterOutlined
} from "@ant-design/icons";
import MobilePullToRefresh from "./MobilePullToRefresh";

const { RangePicker } = DatePicker;

const MobilePatrolDashboard = ({ 
  data, 
  onRefresh,
  patrolCheck = [],
  patrolNormal = [],
  patrolAbnormal = [],
  report = [],
  loading = false
}) => {
  const [showFilter, setShowFilter] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState('');
  
  const handleRefresh = async () => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (onRefresh) {
      await onRefresh();
    }
  };

  // Handle modal display
  const showModal = (data, title, type) => {
    setModalData(data);
    setModalTitle(title);
    setModalType(type);
    setModalVisible(true);
  };

  // Mobile-optimized stats for patrol
  const patrolStats = [
    {
      title: "จุด Check-in",
      value: patrolCheck?.length || 0,
      icon: <AimOutlined />,
      color: "#FFC13F",
      subtitle: "ครั้งทั้งหมด",
      status: "active",
      onClick: () => showModal(patrolCheck, "รายการจุด Check-in", "checkin")
    },
    {
      title: "เหตุการณ์ทั่วไป",
      value: patrolNormal?.length || 0,
      icon: <CheckCircleOutlined />,
      color: "#52c41a",
      subtitle: "เหตุการณ์ปกติ",
      status: "success",
      onClick: () => showModal(patrolNormal, "เหตุการณ์ทั่วไป", "normal")
    },
    {
      title: "เหตุการณ์ผิดปกติ",
      value: patrolAbnormal?.length || 0,
      icon: <ExclamationOutlined />,
      color: "#ff4d4f",
      subtitle: "ต้องติดตาม",
      status: "error",
      onClick: () => showModal(patrolAbnormal, "เหตุการณ์ผิดปกติ", "abnormal")
    },
    {
      title: "รายงานตรวจตรา",
      value: report?.length || 0,
      icon: <UserOutlined />,
      color: "#722ed1",
      subtitle: "รายงานล่าสุด",
      status: "info",
      onClick: () => showModal(report, "รายงานการตรวจตรา", "report")
    }
  ];

  // Calculate patrol efficiency
  const totalPatrols = patrolNormal.length + patrolAbnormal.length;
  const efficiency = totalPatrols > 0 ? Math.round((patrolNormal.length / totalPatrols) * 100) : 100;

  return (
    <MobilePullToRefresh onRefresh={handleRefresh}>
      <div className="mobile-dashboard p-4 pb-20">
        {/* Mobile Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-1">
                🚶‍♂️ ระบบตรวจตรา
              </h2>
              <p className="text-sm text-gray-500">
                อัพเดทล่าสุด: {new Date().toLocaleTimeString('th-TH')}
              </p>
            </div>
            <Button 
              size="small" 
              icon={<FilterOutlined />}
              onClick={() => setShowFilter(!showFilter)}
              className={showFilter ? 'bg-blue-50 text-blue-600' : ''}
            >
              กรอง
            </Button>
          </div>
          
          {/* Mobile Filter Panel */}
          {showFilter && (
            <Card 
              className="mt-3"
              bodyStyle={{ padding: '12px' }}
              style={{
                borderRadius: '8px',
                background: 'rgba(240, 248, 255, 0.8)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
              }}
            >
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">กรองตามวันที่</div>
                <RangePicker
                  size="small"
                  className="w-full"
                  format="DD/MM/YYYY"
                  placeholder={['วันที่เริ่มต้น', 'วันที่สิ้นสุด']}
                />
                <div className="flex space-x-2">
                  <Button size="small" type="primary" className="flex-1">
                    กรองข้อมูล
                  </Button>
                  <Button size="small">
                    รีเซ็ต
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Status Indicator */}
        <Card 
          className="mb-4"
          bodyStyle={{ padding: '12px' }}
          style={{
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(82, 196, 26, 0.2)'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Badge status="processing" />
              <span className="text-sm font-medium">สถานะระบบ</span>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">ประสิทธิภาพ</div>
              <div className="text-lg font-bold text-green-600">{efficiency}%</div>
            </div>
          </div>
          <Progress 
            percent={efficiency} 
            size="small" 
            strokeColor="#52c41a"
            className="mt-2"
            showInfo={false}
          />
        </Card>

        {/* Mobile Stats Grid - 2x2 layout */}
        <Row gutter={[8, 8]} className="mb-4">
          {patrolStats.map((stat, index) => (
            <Col span={12} key={index}>
              <Card 
                className="mobile-stat-card"
                bodyStyle={{ padding: '12px' }}
                style={{
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.98)',
                  border: `1px solid ${stat.color}20`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  cursor: 'pointer'
                }}
                onClick={stat.onClick}
              >
                <div className="flex items-center justify-between mb-2">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: stat.color }}
                  >
                    {stat.icon}
                  </div>
                  {stat.status === 'error' && stat.value > 0 && (
                    <Badge count={stat.value} size="small" />
                  )}
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-800 mb-1">
                    {loading ? "..." : stat.value.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {stat.title}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {stat.subtitle}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Recent Alerts */}
        {patrolAbnormal.length > 0 && (
          <Card 
            title={
              <div className="flex items-center">
                <div className="w-6 h-6 bg-red-500 rounded-lg flex items-center justify-center text-white text-sm mr-2">
                  🚨
                </div>
                <span className="text-sm font-medium">แจ้งเตือนล่าสุด</span>
                <Badge count={patrolAbnormal.length} className="ml-2" />
              </div>
            }
            className="mb-4"
            bodyStyle={{ padding: '12px' }}
            style={{
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.98)'
            }}
          >
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {patrolAbnormal.slice(0, 3).map((alert, index) => (
                <div key={index} className="flex items-center space-x-2 p-2 bg-red-50 rounded-lg">
                  <AlertOutlined className="text-red-500 text-sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800 truncate">
                      {alert.report_name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {alert.report_message?.split('\n')[0]}
                    </div>
                  </div>
                  <Button 
                    size="small" 
                    type="link" 
                    className="text-xs"
                    onClick={() => showModal([alert], `รายละเอียด: ${alert.report_name}`, "abnormal-detail")}
                  >
                    ดู
                  </Button>
                </div>
              ))}
              {patrolAbnormal.length > 3 && (
                <div className="text-center">
                  <Button 
                    size="small" 
                    type="link"
                    onClick={() => showModal(patrolAbnormal, "เหตุการณ์ผิดปกติทั้งหมด", "abnormal")}
                  >
                    ดูทั้งหมด ({patrolAbnormal.length})
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Recent Reports */}
        {report.length > 0 && (
          <Card 
            title={
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center text-white text-sm mr-2">
                  📋
                </div>
                <span className="text-sm font-medium">รายงานล่าสุด</span>
              </div>
            }
            className="mb-4"
            bodyStyle={{ padding: '12px' }}
            style={{
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.98)'
            }}
          >
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {report.slice(0, 3).map((item, index) => (
                <div key={index} className="flex items-center space-x-2 p-2 bg-blue-50 rounded-lg">
                  <CheckCircleOutlined className="text-blue-500 text-sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800 truncate">
                      {item.employee_name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {item.metadata?.split('\n')[0]}
                    </div>
                  </div>
                  <Button 
                    size="small" 
                    type="link" 
                    className="text-xs"
                    onClick={() => showModal([item], `รายงาน: ${item.employee_name}`, "report-detail")}
                  >
                    ดู
                  </Button>
                </div>
              ))}
              {report.length > 3 && (
                <div className="text-center">
                  <Button 
                    size="small" 
                    type="link"
                    onClick={() => showModal(report, "รายงานการตรวจตราทั้งหมด", "report")}
                  >
                    ดูทั้งหมด ({report.length})
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Mobile Quick Actions */}
        <Card 
          title={
            <div className="flex items-center">
              <div className="w-6 h-6 bg-purple-500 rounded-lg flex items-center justify-center text-white text-sm mr-2">
                ⚡
              </div>
              <span className="text-sm font-medium">การดำเนินการด่วน</span>
            </div>
          }
          bodyStyle={{ padding: '12px' }}
          style={{
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.98)'
          }}
        >
          <div className="grid grid-cols-2 gap-2">
            <button 
              className="flex flex-col items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors mobile-haptic"
              onClick={() => showModal([], "แผนที่การตรวจตรา", "map")}
            >
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white mb-1">
                <EyeOutlined />
              </div>
              <span className="text-xs font-medium text-blue-700">ดูแผนที่</span>
            </button>
            <button 
              className="flex flex-col items-center p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors mobile-haptic"
              onClick={() => showModal(patrolCheck, "รายการจุด Check-in", "checkin")}
            >
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center text-white mb-1">
                <AimOutlined />
              </div>
              <span className="text-xs font-medium text-green-700">จุด Check-in</span>
            </button>
            <button 
              className="flex flex-col items-center p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors mobile-haptic"
              onClick={() => showModal([...patrolNormal, ...patrolAbnormal], "ประวัติการตรวจตรา", "history")}
            >
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white mb-1">
                <ClockCircleOutlined />
              </div>
              <span className="text-xs font-medium text-orange-700">ประวัติ</span>
            </button>
            <button 
              className="flex flex-col items-center p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors mobile-haptic"
              onClick={() => showModal(patrolAbnormal, "แจ้งเตือนทั้งหมด", "abnormal")}
            >
              <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center text-white mb-1">
                <ExclamationOutlined />
              </div>
              <span className="text-xs font-medium text-red-700">แจ้งเตือน</span>
            </button>
          </div>
        </Card>

        {/* Data Modal */}
        <Modal
          title={modalTitle}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width="95%"
          style={{ top: 20 }}
          bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '12px' }}
        >
          {modalData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              ไม่มีข้อมูลที่จะแสดง
            </div>
          ) : (
            <List
              dataSource={modalData}
              renderItem={(item, index) => (
                <List.Item key={index} className="border-b border-gray-100 last:border-b-0">
                  {modalType === 'checkin' && (
                    <div className="w-full">
                      <div className="flex items-center space-x-2 mb-2">
                        <AimOutlined className="text-yellow-500" />
                        <span className="font-medium text-sm">จุด Check-in #{index + 1}</span>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>เวลา: {item.created_at ? new Date(item.created_at).toLocaleString('th-TH') : 'N/A'}</div>
                        <div>ตำแหน่ง: {item.location || 'N/A'}</div>
                        <div>สถานะ: {item.status || 'ปกติ'}</div>
                      </div>
                    </div>
                  )}
                  
                  {(modalType === 'normal' || modalType === 'abnormal' || modalType === 'abnormal-detail') && (
                    <div className="w-full">
                      <div className="flex items-start space-x-3">
                        {item.report_img && item.report_img[0] && (
                          <Image
                            width={60}
                            height={60}
                            src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/${item.report_img[0]}`}
                            className="rounded-lg object-cover"
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm mb-1">{item.report_name}</div>
                          <div className="text-xs text-gray-600 mb-2">
                            {item.report_message?.split('\n').map((line, idx) => (
                              <div key={idx}>{line}</div>
                            ))}
                          </div>
                          {item.llm && (
                            <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                              AI Analysis: {item.llm}
                            </div>
                          )}
                          {item.cls && item.cls.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              Detection: {item.cls.map(cls => `${cls}`).join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {(modalType === 'report' || modalType === 'report-detail') && (
                    <div className="w-full">
                      <div className="flex items-start space-x-3">
                        <div className="flex flex-col space-y-2">
                          {item.map_image && (
                            <Image
                              width={60}
                              height={60}
                              src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${item.map_image}`}
                              className="rounded-lg object-cover"
                            />
                          )}
                          {item.employee_image && (
                            <Image
                              width={60}
                              height={60}
                              src={`https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/${item.employee_image}`}
                              className="rounded-lg object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm mb-1">{item.employee_name}</div>
                          <div className="text-xs text-gray-600 mb-2">
                            {item.metadata?.split('\n').map((line, idx) => (
                              <div key={idx}>{line}</div>
                            ))}
                          </div>
                          <div className="text-xs text-gray-500">
                            เวลา: {item.created_at ? new Date(item.created_at).toLocaleString('th-TH') : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {modalType === 'map' && (
                    <div className="w-full text-center py-8">
                      <EnvironmentOutlined className="text-4xl text-blue-500 mb-2" />
                      <div className="text-gray-600">แผนที่การตรวจตรา</div>
                      <div className="text-xs text-gray-500 mt-1">
                        ฟีเจอร์นี้จะแสดงแผนที่เส้นทางการตรวจตรา
                      </div>
                    </div>
                  )}

                  {modalType === 'history' && (
                    <div className="w-full">
                      <div className="flex items-center space-x-2 mb-2">
                        <ClockCircleOutlined className={`${
                          item.report_name ? 'text-red-500' : 'text-green-500'
                        }`} />
                        <span className="font-medium text-sm">
                          {item.report_name || item.employee_name || `เหตุการณ์ #${index + 1}`}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>เวลา: {item.created_at ? new Date(item.created_at).toLocaleString('th-TH') : 'N/A'}</div>
                        <div>ประเภท: {item.report_name ? 'เหตุการณ์ผิดปกติ' : 'เหตุการณ์ทั่วไป'}</div>
                        {item.report_message && (
                          <div>รายละเอียด: {item.report_message.split('\n')[0]}</div>
                        )}
                        {item.metadata && (
                          <div>รายละเอียด: {item.metadata.split('\n')[0]}</div>
                        )}
                      </div>
                    </div>
                  )}
                </List.Item>
              )}
            />
          )}
        </Modal>
      </div>
    </MobilePullToRefresh>
  );
};

export default MobilePatrolDashboard;